/**
 * Passive triggers, from the wiki page Design → Combat rules, section
 * Passives. A passive is a list of `when` / `if` / `do` triggers:
 * - `always` modifiers (`damage_taken_mod`, `healing_received_mod`,
 *   `skip_charge`) are read at the moment of the calculation they affect;
 * - `on_fatal_damage` runs inside `dealDamage` before the holder falls;
 * - `on_damage_taken` runs inside `dealDamage` after the `damaged` event;
 * - `turn_end` runs at the end of the holder's own turn (round.ts).
 * Every trigger that fires emits `passive_triggered`; a trigger whose `do` is
 * only stage changes that would change nothing (`changesNothing`) does not
 * fire. Ordinary effects in `do` are applied to the holder through effects.ts.
 */
import type { Effect, Element, Passive } from '@ab/content-schema';
import { maxHp } from './combat.ts';
import { type ApplyResult, type Ctx, type Step, chain, none } from './commands/types.ts';
import { lookup } from './content.ts';
import { applyEffect } from './effects.ts';
import type { Event } from './events.ts';
import { type RunState, type Unit, findUnit, updateUnit } from './state.ts';
import { stageTotal } from './stats.ts';

export type Style = 'physical' | 'aetherial';

/** What a damage calculation knows about the hit: its element and style (null for status damage). */
export type DamageContext = { readonly element: Element | null; readonly style: Style | null };

export type Trigger = Passive['triggers'][number];
export type Condition = NonNullable<Trigger['if']>;
type DoEntry = Trigger['do'][number];

/** The passive a unit holds, or null when it holds none (a human) or the id is unknown to the content pack. */
export const passiveOf = (ctx: Ctx, unit: Unit): Passive | null => lookup(ctx.content.passives, unit.passive);

/**
 * Whether a trigger's `if` holds for `unit` now. `hpBelow` is strict
 * (`hp × den < maxHp × num`); `damageElement` and `damageStyle` need a damage
 * context and are false without one.
 */
export const conditionHolds = (ctx: Ctx, unit: Unit, cond: Condition | undefined, damage: DamageContext | null): boolean => {
  if (cond === undefined) return true;
  const max = maxHp(ctx, unit);
  if (cond.hpBelow !== undefined && !(unit.hp * cond.hpBelow.den < max * cond.hpBelow.num)) return false;
  if (cond.hpFull === true && unit.hp !== max) return false;
  if (cond.damageElement !== undefined && (damage === null || damage.element !== cond.damageElement)) return false;
  if (cond.damageStyle !== undefined && (damage === null || damage.style !== cond.damageStyle)) return false;
  if (cond.tookDamageThisTurn === true && !unit.tookDamageThisTurn) return false;
  return true;
};

/** The `firedOnce` entry of trigger `index` of a passive: `<passiveId>#<index>`. */
export const onceKey = (passive: Passive, index: number): string => `${passive.id}#${index}`;

/** False when the trigger is once per battle and has already fired this battle. */
const mayFire = (unit: Unit, passive: Passive, trigger: Trigger, index: number): boolean =>
  !(trigger.oncePerBattle === true && unit.firedOnce.includes(onceKey(passive, index)));

/**
 * True when every entry of a trigger's `do` is a `stat_stage` that would leave
 * its stat's total unchanged (already at the −6 or +6 limit). Such a trigger
 * does not fire: no `passive_triggered`, and a once-per-battle trigger is not
 * used up.
 */
const changesNothing = (unit: Unit, trigger: Trigger): boolean =>
  trigger.do.every((d) => {
    if (d.type !== 'stat_stage') return false;
    const total = stageTotal(unit.stages, d.stat);
    return Math.max(-6, Math.min(6, total + d.stages)) === total;
  });

/** The `do` entries of the holder's `always` triggers whose `if` holds now, in data order. */
export const alwaysModifiers = (ctx: Ctx, unit: Unit, damage: DamageContext | null): DoEntry[] => {
  const p = passiveOf(ctx, unit);
  if (p === null) return [];
  const out: DoEntry[] = [];
  for (const t of p.triggers) if (t.when === 'always' && conditionHolds(ctx, unit, t.if, damage)) out.push(...t.do);
  return out;
};

/** A summed percent modifier, and the passive whose modifier matched (null when none did; else `passive_triggered` is emitted). */
export type PercentMod = { readonly sum: number; readonly passive: string | null };

/** Sum of the holder's matching `damage_taken_mod` percents for this hit. A mod's `style` / `element`, when set, must match. */
export const damageTakenMod = (ctx: Ctx, unit: Unit, damage: DamageContext): PercentMod => {
  let sum = 0;
  let matched = false;
  for (const m of alwaysModifiers(ctx, unit, damage)) {
    if (m.type !== 'damage_taken_mod') continue;
    if (m.style !== undefined && m.style !== damage.style) continue;
    if (m.element !== undefined && m.element !== damage.element) continue;
    sum += m.percent;
    matched = true;
  }
  return { sum, passive: matched ? unit.passive : null };
};

/** Sum of the holder's `healing_received_mod` percents, checked before the heal. */
export const healingReceivedMod = (ctx: Ctx, unit: Unit): PercentMod => {
  let sum = 0;
  let matched = false;
  for (const m of alwaysModifiers(ctx, unit, null)) {
    if (m.type !== 'healing_received_mod') continue;
    sum += m.percent;
    matched = true;
  }
  return { sum, passive: matched ? unit.passive : null };
};

/** The holder's passive when it has an `always` `skip_charge` for skills of `style`, else null. */
export const skipsCharge = (ctx: Ctx, unit: Unit, style: Style): string | null =>
  alwaysModifiers(ctx, unit, null).some((m) => m.type === 'skip_charge' && m.style === style) ? unit.passive : null;

/**
 * `passive_triggered`, and, when the trigger is once per battle, its key
 * `<passiveId>#<index>` recorded in `firedOnce` (so each trigger of a passive
 * is limited on its own).
 */
export const markFired = (state: RunState, unitId: string, passive: Passive, trigger: Trigger, index: number): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null) return none(state);
  const event: Event = { type: 'passive_triggered', unit: unitId, passive: passive.id };
  const key = onceKey(passive, index);
  if (trigger.oncePerBattle !== true || u.firedOnce.includes(key)) return { state, events: [event] };
  return { state: updateUnit(state, unitId, (x) => ({ ...x, firedOnce: [...x.firedOnce, key] })), events: [event] };
};

const isEffect = (d: DoEntry): d is Effect =>
  d.type !== 'damage_taken_mod' && d.type !== 'healing_received_mod' && d.type !== 'survive_fatal' && d.type !== 'skip_charge';

/** Applies the ordinary effects of a trigger's `do` to the holder, in order. Modifiers are skipped (they are read elsewhere). */
export const applyTriggerEffects = (ctx: Ctx, state: RunState, unitId: string, trigger: Trigger): ApplyResult =>
  chain(
    state,
    ...trigger.do.filter(isEffect).map(
      (e): Step =>
        (s) => {
          const u = findUnit(s, unitId);
          if (u === null || u.down) return none(s);
          return applyEffect(ctx, s, e, { unit: unitId, style: null, element: null, falloffPower: 0, origin: 'passive' }, { unit: unitId, pos: u.pos, distance: 0 });
        },
    ),
  );

/**
 * The `on_fatal_damage` triggers that fire for a hit that would take the
 * holder to 0 hp, with `if` evaluated before the damage. Returns the hp the
 * holder keeps (the highest `survive_fatal` `hpLeft`, capped at its current
 * hp; 0 when nothing saves it), the triggers that fired (their ordinary
 * effects run after the damage), and `passive_triggered` for each.
 */
export const onFatalDamage = (
  ctx: Ctx,
  state: RunState,
  unitId: string,
  damage: DamageContext,
): { readonly result: ApplyResult; readonly hpFloor: number; readonly fired: readonly Trigger[] } => {
  const u = findUnit(state, unitId);
  const p = u === null ? null : passiveOf(ctx, u);
  if (u === null || p === null) return { result: none(state), hpFloor: 0, fired: [] };
  let s = state;
  const events: Event[] = [];
  const fired: Trigger[] = [];
  let hpFloor = 0;
  for (const [i, t] of p.triggers.entries()) {
    if (t.when !== 'on_fatal_damage') continue;
    const cur = findUnit(s, unitId) ?? u;
    if (!mayFire(cur, p, t, i) || !conditionHolds(ctx, u, t.if, damage) || changesNothing(cur, t)) continue;
    for (const d of t.do) if (d.type === 'survive_fatal') hpFloor = Math.max(hpFloor, Math.min(d.hpLeft, u.hp));
    const m = markFired(s, unitId, p, t, i);
    s = m.state;
    events.push(...m.events);
    fired.push(t);
  }
  return { result: { state: s, events }, hpFloor, fired };
};

/**
 * Runs the holder's triggers for `when` (`on_damage_taken` or `turn_end`), in
 * data order: each whose `if` holds (and, if once per battle, has not fired)
 * emits `passive_triggered` and applies its `do` effects to the holder.
 * Stops when the holder is gone or down.
 */
export const runTrigger = (
  ctx: Ctx,
  state: RunState,
  unitId: string,
  when: 'on_damage_taken' | 'turn_end',
  damage: DamageContext | null,
): ApplyResult => {
  const start = findUnit(state, unitId);
  const p = start === null ? null : passiveOf(ctx, start);
  if (p === null) return none(state);
  return chain(
    state,
    ...p.triggers.map(
      (t, i): Step =>
        (s) => {
          if (t.when !== when) return none(s);
          const u = findUnit(s, unitId);
          if (u === null || u.down || !mayFire(u, p, t, i) || !conditionHolds(ctx, u, t.if, damage) || changesNothing(u, t)) return none(s);
          return chain(
            s,
            (x) => markFired(x, unitId, p, t, i),
            (x) => applyTriggerEffects(ctx, x, unitId, t),
          );
        },
    ),
  );
};

/** `turn_end` triggers of one unit, run at the end of its own turn (round.ts). */
export const runTurnEnd = (ctx: Ctx, state: RunState, unitId: string): ApplyResult => runTrigger(ctx, state, unitId, 'turn_end', null);
